import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Smartphone, SprayCan, Shirt, KeyRound, Truck, MapPin, Wrench, Car, Flame, Bike,
  Search, CalendarCheck, ShieldCheck, Star, MessageCircle, ArrowRight, ArrowUpRight, Send,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { StoreButtons } from "@/components/AppStoreButton";
import { api, formatApiError, DRIVER_APP_URL } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const iconMap = {
  gas: Flame,
  car: Car,
  motorbike: Bike,
  "device-repair": Smartphone,
  cleaning: SprayCan,
  laundry: Shirt,
  "keys-locks": KeyRound,
  "pickup-delivery": Truck,
  "on-site-visit": MapPin,
  wrench: Wrench,
};
const stepIcons = [Search, CalendarCheck, ShieldCheck];
const marqueeItems = ["Gas", "Car", "Motorbike", "Device Repair", "Keys", "Pickup & Delivery", "On-site", "Secure Payment"];

function Hero() {
  const { t } = useI18n();
  return (
    <section id="download" className="relative overflow-hidden pt-24 pb-12 sm:pt-28 sm:pb-14 lg:pt-36 lg:pb-20">
      <div className="pointer-events-none absolute -right-48 -top-24 -z-10 h-[420px] w-[420px] rounded-full bg-giva-surface2 blur-3xl sm:-right-40 sm:-top-20 sm:h-[500px] sm:w-[500px]" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="min-w-0 lg:col-span-6"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-giva-line bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-giva-text2">
            <span className="h-2 w-2 rounded-full bg-giva-accent" /> {t("hero.badge")}
          </span>
          <h1 className="mt-6 max-w-full break-words font-display text-[2.65rem] font-black leading-[1.04] tracking-tight text-giva-navy sm:text-6xl lg:text-7xl">
            {t("hero.title1")}<br />
            <span className="text-giva-primary">{t("hero.title2")}</span>
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-giva-text2 sm:mt-7 sm:text-lg">{t("hero.subtitle")}</p>
          <div className="mt-8 max-w-xl sm:mt-9">
            <StoreButtons testPrefix="hero" />
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3 text-sm text-giva-text2 sm:gap-6">
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-giva-accent text-giva-accent" />)}
            </div>
            <span className="min-w-0 flex-1">{t("hero.rating")}</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="min-w-0 lg:col-span-6"
        >
          <div className="relative">
            <div className="overflow-hidden rounded-[2rem] border border-giva-line shadow-xl">
              <img
                src="https://images.pexels.com/photos/3769121/pexels-photo-3769121.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Person booking a service through the Giva app"
                className="h-[300px] w-full object-cover sm:h-[420px] lg:h-[520px]"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden w-60 rounded-2xl border border-giva-line bg-white p-5 shadow-lg sm:block">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-giva-primary text-white">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-display text-sm font-bold text-giva-navy">{t("hero.secure")}</p>
                  <p className="text-xs text-giva-text2">{t("hero.secureSub")}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  return (
    <div className="overflow-hidden border-y border-giva-line/70 bg-giva-surface2/70 py-3">
      <div className="giva-marquee flex w-max items-center gap-10 whitespace-nowrap">
        {[...marqueeItems, ...marqueeItems].map((item, i) => (
          <span key={i} className="font-display text-lg font-bold uppercase tracking-tight text-giva-navy/80 sm:text-xl">
            {item} <span className="text-giva-accent">/</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Services() {
  const { t, lang } = useI18n();
  const fallbackItems = useMemo(
    () => t("services.items").map((item, i) => ({ ...item, icon_key: ["gas", "car", "motorbike", "device-repair", "keys-locks", "pickup-delivery", "on-site-visit"][i] })),
    [t]
  );
  const [items, setItems] = useState(fallbackItems);

  useEffect(() => {
    let active = true;
    api.get("/service-categories")
      .then((r) => {
        if (!active || !Array.isArray(r.data) || r.data.length === 0) return;
        setItems(r.data.map((c) => ({
          title: c[`title_${lang}`] || c.title_en || c.title_pt || c.key,
          desc: c[`desc_${lang}`] || c.desc_en || c.desc_pt || "",
          icon_key: c.icon_key || c.key,
        })));
      })
      .catch(() => { if (active) setItems(fallbackItems); });
    return () => { active = false; };
  }, [fallbackItems, lang]);

  return (
    <section id="servicos" className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
      <div className="max-w-2xl">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-giva-text2">{t("services.overline")}</span>
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-giva-navy sm:text-4xl lg:text-5xl">{t("services.title")}</h2>
        <p className="mt-5 text-lg text-giva-text2">{t("services.subtitle")}</p>
      </div>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((s, i) => {
          const Icon = iconMap[s.icon_key] || Wrench;
          return (
            <div
              key={i}
              data-testid={`service-card-${i}`}
              className="group rounded-2xl border border-giva-line bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-giva-surface2 text-giva-primary transition-colors group-hover:bg-giva-primary group-hover:text-white">
                <Icon className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-display text-xl font-bold text-giva-navy">{s.title}</h3>
              <p className="mt-2.5 text-base leading-relaxed text-giva-text2">{s.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useI18n();
  const items = t("steps.items");
  return (
    <section id="como-funciona" className="bg-giva-surface2 py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-giva-text2">{t("steps.overline")}</span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-giva-navy sm:text-4xl lg:text-5xl">{t("steps.title")}</h2>
        </div>
        <div className="mt-10 divide-y divide-giva-line rounded-[2rem] border border-giva-line bg-white">
          {items.map((s, i) => {
            const Icon = stepIcons[i];
            return (
              <div key={i} className="grid gap-5 p-6 sm:grid-cols-[auto_1fr_auto] sm:items-center lg:p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-giva-primary text-white">
                  <Icon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-xl font-bold text-giva-navy">{s.title}</h3>
                  <p className="mt-2 max-w-2xl text-base leading-relaxed text-giva-text2">{s.desc}</p>
                </div>
                <span className="font-display text-3xl font-black text-giva-line">{String(i + 1).padStart(2, "0")}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProviderCTA() {
  const { t } = useI18n();
  return (
    <section className="mx-auto max-w-7xl px-5 py-14 lg:px-8 lg:py-20">
      <div className="overflow-hidden rounded-[2rem] bg-giva-navy px-6 py-9 shadow-sm lg:grid lg:grid-cols-12 lg:items-center lg:gap-10 lg:px-12 lg:py-12">
        <div className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-white/80">
            <MessageCircle className="h-4 w-4" /> {t("provider.badge")}
          </span>
          <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">{t("provider.title")}</h2>
          <p className="mt-5 max-w-md text-lg text-white/70">{t("provider.subtitle")}</p>
          <a
            href="/#contacto"
            data-testid="provider-register-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-giva-accent px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03] hover:bg-giva-accentHover"
          >
            {t("provider.cta")} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <div className="mt-8 lg:col-span-6 lg:mt-0">
          <img
            src="https://images.unsplash.com/photo-1721332154191-ba5f1534266e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            alt="Giva professional at work"
            className="h-64 w-full rounded-2xl object-cover lg:h-72"
          />
        </div>
      </div>
    </section>
  );
}

function DriverEarnCTA() {
  const { t } = useI18n();
  return (
    <section className="bg-giva-surface2 py-14 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-giva-line bg-white shadow-sm lg:grid lg:grid-cols-12 lg:items-center">
          <div className="p-6 lg:col-span-6 lg:p-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-giva-surface2 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-giva-text2">
              <Truck className="h-4 w-4" /> {t("driverEarn.badge")}
            </span>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-giva-navy sm:text-4xl lg:text-5xl">{t("driverEarn.title")}</h2>
            <p className="mt-5 max-w-md text-lg text-giva-text2">{t("driverEarn.subtitle")}</p>
            <a
              href={DRIVER_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="driver-earn-app-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-giva-primary px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03] hover:bg-giva-primaryHover"
            >
              {t("driverEarn.cta")} <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <div className="bg-giva-surface2 p-5 lg:col-span-6 lg:h-full lg:p-8">
            <img
              src="/assets/giva-driver-delivery.png"
              alt="Giva Driver delivery branding"
              className="h-72 w-full rounded-2xl object-contain object-center lg:h-96"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function BlogPreview() {
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blog/posts").then((r) => setPosts(Array.isArray(r.data) ? r.data.slice(0, 3) : [])).catch(() => setPosts([]));
  }, []);
  if (posts.length === 0) return null;
  return (
    <section className="bg-giva-surface2 py-14 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex items-end justify-between">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-giva-text2">{t("blogPreview.overline")}</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-giva-navy sm:text-4xl lg:text-5xl">{t("blogPreview.title")}</h2>
          </div>
          <Link to="/blog" data-testid="blog-see-all-link" className="hidden items-center gap-2 text-sm font-semibold text-giva-primary hover:text-giva-primaryHover sm:inline-flex">
            {t("blogPreview.seeAll")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {posts.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-preview-${p.slug}`} className="group flex flex-col overflow-hidden rounded-2xl border border-giva-line bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
              {p.cover_image && <img src={p.cover_image} alt={p.title} className="h-48 w-full object-cover" />}
              <div className="flex flex-1 flex-col p-6">
                <span className="text-xs font-semibold uppercase tracking-wider text-giva-accent">{p.category}</span>
                <h3 className="mt-3 font-display text-lg font-bold leading-snug text-giva-navy group-hover:text-giva-primary">{p.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-giva-text2">{p.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-giva-text2">{t("contact.overline")}</span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-giva-navy sm:text-4xl lg:text-5xl">{t("contact.title")}</h2>
          <p className="mt-5 max-w-md text-lg text-giva-text2">{t("contact.subtitle")}</p>
          <p className="mt-4 max-w-md rounded-2xl border border-giva-line bg-white px-4 py-3 text-sm leading-relaxed text-giva-text2">{t("contact.providerHint")}</p>
        </div>
        <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-giva-line bg-white p-6">
          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium text-giva-text">{t("contact.name")}</label>
              <input
                required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name-input"
                className="mt-2 w-full rounded-xl border border-giva-line bg-giva-bg px-4 py-3 text-base outline-none transition-colors focus:border-giva-primary"
                placeholder={t("contact.namePh")}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-giva-text">{t("contact.email")}</label>
              <input
                required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contact-email-input"
                className="mt-2 w-full rounded-xl border border-giva-line bg-giva-bg px-4 py-3 text-base outline-none transition-colors focus:border-giva-primary"
                placeholder={t("contact.emailPh")}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-giva-text">{t("contact.message")}</label>
              <textarea
                required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message-input"
                className="mt-2 w-full resize-none rounded-xl border border-giva-line bg-giva-bg px-4 py-3 text-base outline-none transition-colors focus:border-giva-primary"
                placeholder={t("contact.messagePh")}
              />
            </div>
            <button
              type="submit" disabled={loading} data-testid="contact-submit-btn"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-giva-primary px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.01] hover:bg-giva-primaryHover disabled:opacity-60"
            >
              {loading ? t("contact.sending") : <>{t("contact.send")} <Send className="h-4 w-4" /></>}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-giva-bg">
      <SiteHeader />
      <main>
        <Hero />
        <Marquee />
        <Services />
        <HowItWorks />
        <ProviderCTA />
        <DriverEarnCTA />
        <BlogPreview />
        <Contact />
      </main>
      <SiteFooter />
    </div>
  );
}
